import { useState } from 'react';
import { useSessionStore } from '@/state/session-store';
import { Reminders } from './Reminders';
import type { SessionTiming } from '@/types';

interface Props {
  onContinue: () => void;
  onSkip?: () => void;
}

const DURATION_OPTIONS = [15, 30, 45, 60];
const MAX_TIME_OPTIONS = [
  { value: 60, label: '60 min' },
  { value: 90, label: '90 min' },
  { value: 120, label: '2 hrs' },
  { value: 0, label: 'Off' },
];

export function SessionSetup({ onContinue, onSkip }: Props) {
  const {
    coachingSettings,
    updateCoachingSettings,
    reminders,
    addReminder,
    toggleReminder,
    deleteReminder,
    setSessionTiming,
  } = useSessionStore();

  const [duration, setDuration] = useState(coachingSettings.defaultDurationMin);
  const [customDuration, setCustomDuration] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [halfwayAlert, setHalfwayAlert] = useState(coachingSettings.enableHalfwayAlert);
  const [fiveMinAlert, setFiveMinAlert] = useState(coachingSettings.enableFiveMinAlert);
  const [maxCallTime, setMaxCallTime] = useState(coachingSettings.maxCallTimeMin);

  const handleDurationChange = (value: number | 'custom') => {
    if (value === 'custom') {
      setIsCustom(true);
      setCustomDuration(String(duration));
    } else {
      setIsCustom(false);
      setDuration(value);
    }
  };

  const handleContinue = () => {
    const finalDuration = isCustom ? parseInt(customDuration) || 30 : duration;

    const timing: SessionTiming = {
      scheduledDurationMin: finalDuration,
      alerts: {
        halfway: halfwayAlert,
        fiveMinWarning: fiveMinAlert,
      },
    };

    // Update max call time in coaching settings
    updateCoachingSettings({ maxCallTimeMin: maxCallTime });

    setSessionTiming(timing);
    onContinue();
  };

  return (
    <div className="space-y-5">
      <div className="text-center mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Session Setup</h3>
        <p className="text-xs text-gray-500 mt-1">Configure your meeting before starting</p>
      </div>

      {/* Meeting Duration */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Meeting Duration
        </label>
        <div className="grid grid-cols-4 gap-2">
          {DURATION_OPTIONS.map(opt => (
            <button
              key={opt}
              onClick={() => handleDurationChange(opt)}
              className={`py-2 px-3 text-sm rounded-md border transition-colors ${
                !isCustom && duration === opt
                  ? 'bg-green-500 text-white border-green-500'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-green-300'
              }`}
            >
              {opt} min
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => handleDurationChange('custom')}
            className={`py-2 px-3 text-sm rounded-md border transition-colors ${
              isCustom
                ? 'bg-green-500 text-white border-green-500'
                : 'bg-white text-gray-700 border-gray-200 hover:border-green-300'
            }`}
          >
            Custom:
          </button>
          <input
            type="number"
            value={customDuration}
            onChange={(e) => {
              setCustomDuration(e.target.value);
              setIsCustom(true);
            }}
            onFocus={() => setIsCustom(true)}
            placeholder="45"
            min="5"
            max="180"
            className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-green-500"
          />
          <span className="text-sm text-gray-500">min</span>
        </div>
      </div>

      {/* Time Alerts */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Time Alerts
        </label>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={halfwayAlert}
              onChange={(e) => setHalfwayAlert(e.target.checked)}
              className="rounded border-gray-300 text-green-500 focus:ring-green-500"
            />
            <span className="text-sm text-gray-700">Alert me at halfway point</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={fiveMinAlert}
              onChange={(e) => setFiveMinAlert(e.target.checked)}
              className="rounded border-gray-300 text-green-500 focus:ring-green-500"
            />
            <span className="text-sm text-gray-700">Alert me at 5 minutes remaining</span>
          </label>
        </div>
      </div>

      {/* Max Call Time (Safety Limit) */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Max Recording Time
        </label>
        <p className="text-xs text-gray-400 -mt-1">
          Auto-ends session to prevent forgotten recordings
        </p>
        <div className="grid grid-cols-4 gap-2">
          {MAX_TIME_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setMaxCallTime(opt.value)}
              className={`py-2 px-3 text-sm rounded-md border transition-colors ${
                maxCallTime === opt.value
                  ? 'bg-green-500 text-white border-green-500'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-green-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Reminders */}
      <div className="border-t border-gray-100 pt-4">
        <Reminders
          reminders={reminders}
          onToggle={toggleReminder}
          onDelete={deleteReminder}
          onAdd={addReminder}
          readOnly={false}
        />
        <p className="text-xs text-gray-400 mt-2">
          Add questions from last call, topics to cover, or follow-up items
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        {onSkip && (
          <button
            onClick={onSkip}
            className="flex-1 py-2.5 px-4 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleContinue}
          className="flex-1 py-2.5 px-4 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors"
        >
          Save Settings
        </button>
      </div>
    </div>
  );
}
